import {
	SumpTdhHelp,
	PumpSizingFormData,
	StandardTdhHelp,
	TdhHelpBase,
} from "../../types/types";
import { getFittingEquivalentLength } from "../../data/tdh";
import { formatNumericValue, parseNumericValue } from "./helpers";

export function calculateTotalPipeLength(help: TdhHelpBase): number {
	const horizontalLength = parseNumericValue(help.horizontalLength, 0);

	if (!help.pipeDiameter) {
		return 0;
	}

	const fittingLength = (
		Object.keys(help.fittings) as Array<keyof TdhHelpBase["fittings"]>
	).reduce((total, fittingKey) => {
		const count = parseNumericValue(help.fittings[fittingKey], 0);
		const multiplier = getFittingEquivalentLength(
			help.pipeDiameter,
			fittingKey,
		);
		return total + count * multiplier;
	}, 0);

	return horizontalLength + fittingLength;
}

export function getTotalPipeLengthValue(help: TdhHelpBase): string {
	return formatNumericValue(calculateTotalPipeLength(help));
}

export function applyTotalPipeLength<T extends TdhHelpBase>(help: T): T {
	return {
		...help,
		totalLengthOfPipe: getTotalPipeLengthValue(help),
	};
}

export function getTdhDisplayValue(formData: PumpSizingFormData): string {
	const { tdhData } = formData;

	if (tdhData.mode === "known") {
		return tdhData.knownTdh.trim();
	}

	return tdhData.calculatedTdh.trim();
}

/**
 * The help values behind the calculator, or null when there are none to read:
 * either no application type yet, or the user entered a TDH directly.
 */
export function getActiveTdhHelp(
	formData: PumpSizingFormData,
): TdhHelpBase | null {
	const { applicationType, tdhData } = formData;

	if (!applicationType || tdhData.mode === "known") {
		return null;
	}

	return applicationType === "sump" ? tdhData.sump : tdhData.standard;
}

export function isTdhStepValid(formData: PumpSizingFormData): boolean {
	const { applicationType, tdhData } = formData;

	if (!applicationType) {
		return false;
	}

	if (tdhData.mode === "known") {
		return tdhData.knownTdh.trim() !== "";
	}

	switch (applicationType) {
		case "sump":
			return isSumpTdhHelpValid(tdhData.sump);
		case "septic":
		case "sewage":
			return isStandardTdhHelpValid(tdhData.standard);
		default:
			return false;
	}
}

function isStandardTdhHelpValid(help: StandardTdhHelp): boolean {
	return isTdhHelpBaseValid(help);
}

function isSumpTdhHelpValid(help: SumpTdhHelp): boolean {
	if (help.dischargeIntoForceMain && help.psi.trim() === "") {
		return false;
	}

	return isTdhHelpBaseValid(help);
}

function isTdhHelpBaseValid(help: TdhHelpBase): boolean {
	if (
		help.staticHead.trim() === "" ||
		help.pipeDiameter === "" ||
		help.pipeMaterial === "" ||
		parseNumericValue(help.horizontalLength, 0) <= 0
	) {
		return false;
	}

	return parseNumericValue(help.totalLengthOfPipe, 0) > 0;
}
